<template>
  <div class="headerNavigation">
    <aside class="headerNavigation__CategoryList">
      <div class="headerNavigation__AsideTop">
        <button @click="closeNavigation" class="headerNavigation__CloseButton">
          <img src="/resources/images/icons/close.svg" width="24" height="24" :alt="$t('close')" />
        </button>

        <div class="headerNavigation__SelectFields">
          <div class="headerNavigation__LanguageSelector" :class="{'isActive': languageSelectorOpen}" v-click-outside="() => closeLanguageSelector()">
            <button aria-label="Select language" @click="languageSelectorOpen = !languageSelectorOpen">
              <Icon name="globe" />
              <span>{{ selectedLanguage }}</span>
              <Icon class="headerNavigation__LanguageArrowIcon" name="down" />
            </button>

            <ul v-if="page?.language_switcher" class="headerNavigation__LanguageOptions">
              <li v-for="(link, key) of page.language_switcher">
                <nuxt-link :to="{ path: link, query: filterQueries(useRouter().currentRoute.value.query) }" :class="{ 'isActive': selectedLanguage === key }">{{ $t(`languages.${ key }`) }}</nuxt-link>
              </li>
            </ul>
          </div>
          <div v-if="false">
            <SelectField class="headerNavigation__CurrencySelector" v-model="selectedCurrency" :options="currencyOptions" :without-border="true"></SelectField>
          </div>
        </div>
      </div>

      <div class="headerNavigation__Categories">
        <ul>
          <li v-for="item in menuItems">
            <button v-if="item.data && Object.keys(item.data)" type="button" @click="setMenuCategory(item)" :class="{'isActive': activeCategory?.id === item.id}">{{ item.title }}</button>
            <nuxt-link v-else-if="item.url" :to="item.url">{{ item.title }}</nuxt-link>
          </li>
        </ul>

        <ul class="headerNavigation__BottomItems">
          <li v-for="item in menuBottomItems">
            <nuxt-link :to="item.url" :external="item.external" :target="item.external ? '_blank' : ''">{{ item.title }}</nuxt-link>
          </li>
        </ul>
      </div>
    </aside>

    <div class="headerNavigation__ItemsListWrapper" :class="[{isActive: activeCategory}, activeCategory?.type ? activeCategory.type : '']">
      <transition name="fade" mode="out-in">
        <div v-show="activeCategory">
          <div class="headerNavigation__SubcategoryList">
            <button type="button" @click="activeCategory = null" class="headerNavigation__StepBackButton">
              <img src="/resources/images/icons/back.svg" width="24" height="24" />
            </button>
            <template v-for="category in menuItems">
              <template v-if="category?.data?.children?.length">
                <div v-show="activeCategory?.id === category.id" :key="category.id">
                  <ul>
                    <li v-for="subcategoryItem in category.data.children">
                      <button type="button" @click="setSubcategory(subcategoryItem)" :class="{'isActive': activeCategory?.activeSubcategory?.id === subcategoryItem.id}">{{ subcategoryItem.name }}</button>
                    </li>
                  </ul>
            
                  <div v-if="activeCategory?.activeSubcategory?.place?.menu_image" class="headerNavigation__ImageWrapper">
                    <Flare :srcset="activeCategory.activeSubcategory.place.menu_image" size="both" width="160" height="160" crop="fill" :key="activeCategory?.activeSubcategory.id" :alt="activeCategory.activeSubcategory.place.menu_image" />
                  </div>

                  <nuxt-link v-if="category.data?.sef" :to="category.data.sef" class="headerNavigation__MoreInfoButton">
                    <span>{{ $t('allDestinations') }}</span>
                    <img src="/resources/images/icons/arrow-right.svg" width="24" height="24" />
                  </nuxt-link>
                </div>
              </template>
            </template>
          </div>
  
          <div class="headerNavigation__ItemsList" :class="{'isActive': activeCategory?.activeSubcategory}">
            <button type="button" @click="unsetSubcategory" class="headerNavigation__StepBackButton">
              <img src="/resources/images/icons/back.svg" width="24" height="24" />
            </button>
  
            <p>
              <template v-if="activeCategory?.id === 'hotels'">{{ $t('hotelsAndResorts') }} </template>
              <template v-else-if="activeCategory?.id === 'camps'">{{ $t('campsites') }}</template>
              {{ $t('in') }} {{ activeCategory?.activeSubcategory?.name }}
            </p>
            
            <template v-for="category in menuItems">
              <template v-if="category?.data?.children?.length">
                <div v-for="subcategoryItem in category.data.children" :class="{'isActive': activeCategory?.id === category.id && activeCategory?.activeSubcategory?.id === subcategoryItem.id}">
                  <ul v-if="subcategoryItem.children?.length">
                    <li v-for="lastLayer in subcategoryItem.children">
                      <nuxt-link :to="lastLayer.sef">
                        {{ lastLayer.name }}
                        <div v-if="lastLayer.category_rating && activeCategory" class="headerNavigation__StarsWrapper">
                          <div v-for="star in lastLayer.category_rating">
                            <img src="/resources/images/icons/star.svg" width="14" height="14" alt="Star" />
                          </div>
                        </div>
                      </nuxt-link>
                    </li>
                  </ul>

                  <nuxt-link :to="subcategoryItem.sef" class="headerNavigation__MoreInfoButton">
                    <span class="desktop">{{ $t(`${ activeCategory?.id === 'camps' ? 'allCampsites' : 'searchAllHotels'}`) }}</span>
                    <span class="mobile">{{ $t('searchAll') }}</span>
                    <img src="/resources/images/icons/arrow-right.svg" width="24" height="24" />
                  </nuxt-link>
                </div>
              </template>
            </template>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  const { page } = usePage();
  const { link } = useSetup();
  const { t } = useI18n();
  const { lang } = useLanguage();
  const { initialData } = useSetup();
  const menuOpen = useState('menuOpen');
  const activeCategory = ref(null);

  const selectedLanguage = ref('hr');
  const languageSelectorOpen = ref(false);
  
  const selectedCurrency = ref('eur');
  const currencyOptions = [
    {
      title: 'EUR',
      value: 'eur',
    },
    {
      title: 'USD',
      value: 'usd',
    },
  ];

  const locationsData = initialData.value.accommodation_kind_group_pages.reduce((acc, accItem) => {
    const isHotel = accItem.accommodation_kind_group.items.includes('HOTEL');
    acc[isHotel ? 'hotels' : 'camps'] = accItem;
    return acc;
  }, {hotels: [], camps: []});

  const menuItems = computed(() => [
    {id: 'hotels', title: t('menu.hotels'), data: locationsData.hotels, type: 'list'},
    {id: 'camps', title: t('menu.camps'), data: locationsData.camps, type: 'list'},
    {id: 'specialOffers', title: t('menu.specialOffers'), url: link('OfferList')},
    {id: 'destinations', title: t('menu.destinations'), url: link('PlaceList')},
    // {id: 'holidayTypes', title: t('menu.holidayTypes'), type: 'grid', children: initialData.value.amenities.holiday_types || ''},
    {id: 'holidayTypes', title: t('menu.holidayTypes'), url: link('HolidayTypeList')},
  ])

  const menuBottomItems = computed(() => [
    { title: t('menu.brands'), url: link('BrandList') },
    { title: t('menu.loyalty'), url: link('Loyalty') },
    { title: t('menu.blogs'), url: link('BlogList') },
  ])

  function closeLanguageSelector() {
    languageSelectorOpen.value = false;
  }

  function closeNavigation() {
    menuOpen.value = false;
    activeCategory.value = null;
    document.documentElement.classList.remove('isMenuOpen');
  }

  function setMenuCategory(item) {
    if (activeCategory.value?.activeSubcategory) unsetSubcategory();
    activeCategory.value = item;

    if (window?.innerWidth > 768 && item.data?.children?.length && item.type === 'list') {
      setTimeout(() => {
        activeCategory.value.activeSubcategory = item.data.children[0];
      }, 100);
    }
  }

  function setSubcategory(subcategoryItem) {
    activeCategory.value.activeSubcategory = subcategoryItem;
  }

  function unsetSubcategory() {
    delete activeCategory.value.activeSubcategory;
  }

  function filterQueries(queries) {
    if (!queries || !Object.keys(queries).length) return;
    let _queries = JSON.parse(JSON.stringify(queries));
    const unAcceptableQueries = ['_affclk', 'fbclid', 'fs', 'gad_source', 'gbraid', 'gclid', 'id_m', 'package', 'partners_access', 'r', 's', 'sa', 'sc_lid', 'sc_llid', 'sc_src', 'sc_uid', 'source', 'src', 'storeId', 'turneoExperience', 'unit_id', 'utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'ved', 'yandex-source']

    for (const key in _queries) {
      if (unAcceptableQueries.includes(key)) delete _queries[key];
    }
    
    return _queries;
  }

  onMounted(() => {
    selectedLanguage.value = lang.value;
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_headerNavigation.scss');
</style>